import { useState } from "react";
import { mockInvoices } from "@/mocks/dealerData";

type TabType = "e-archive" | "e-invoice";

export default function InvoicesPage() {
  const [activeTab, setActiveTab] = useState<TabType>("e-archive");
  const [search, setSearch] = useState("");

  const filtered = mockInvoices.filter((inv) => {
    if (inv.type !== activeTab) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return inv.invoiceNumber.toLowerCase().includes(q) || inv.customerName.toLowerCase().includes(q);
  });

  const totalAmount = filtered.reduce((sum, inv) => sum + inv.amount, 0);

  const statusColors: Record<string, string> = {
    "Onaylandı": "bg-primary-100 text-primary-700",
    "Beklemede": "bg-accent-100 text-accent-700",
    "Gönderildi": "bg-secondary-100 text-secondary-700",
    "İptal": "bg-foreground-100 text-foreground-500",
  };

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div>
          <h4 className="text-lg font-semibold text-foreground-950"><a id="invoices-title">Faturalar</a></h4>
          <p className="text-sm text-foreground-500 mt-0.5">e-Arşiv ve e-Fatura işlemlerinizi yönetin</p>
        </div>
        <div className="relative w-full sm:w-64">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Fatura ara..." className="w-full pl-9 pr-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400" />
        </div>
      </div>

      {/* Tab switcher */}
      <div className="flex items-center gap-1 bg-background-100 rounded-full p-1 w-fit mb-6">
        {[{ key: "e-archive", label: "e-Arşiv Fatura" }, { key: "e-invoice", label: "e-Fatura" }].map((tab) => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key as TabType)} className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === tab.key ? "bg-background-50 text-foreground-950" : "text-foreground-500 hover:text-foreground-700"}`}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Summary */}
      <div className="flex items-center gap-4 mb-4 text-sm">
        <span className="text-foreground-500">Toplam {filtered.length} fatura</span>
        <span className="text-foreground-500">|</span>
        <span className="text-foreground-500">Toplam Tutar: <strong className="text-foreground-950">${totalAmount.toLocaleString("en-US")}</strong></span>
      </div>

      {/* Invoices table */}
      <div className="bg-background-50 border border-background-200/70 rounded-xl overflow-hidden">
        {/* Desktop Table */}
        <div className="overflow-x-auto hidden md:block">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="bg-background-100 text-left">
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Fatura No</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Müşteri</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-right">Tutar</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Tarih</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500">Durum</th>
                <th className="px-4 py-3 text-xs font-medium text-foreground-500 text-center">İşlem</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inv) => (
                <tr key={inv.id} className="border-t border-background-200/70 hover:bg-background-100/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-foreground-900">{inv.invoiceNumber}</td>
                  <td className="px-4 py-3 text-foreground-600">{inv.customerName}</td>
                  <td className="px-4 py-3 text-foreground-900 text-right font-medium">${inv.amount.toLocaleString("en-US")}</td>
                  <td className="px-4 py-3 text-foreground-600 text-xs">{new Date(inv.date).toLocaleDateString("tr-TR")}</td>
                  <td className="px-4 py-3"><span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[inv.status] || "bg-background-100 text-foreground-600"}`}>{inv.status}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1">
                      <button className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer" title="Görüntüle"><i className="ri-eye-line text-sm"></i></button>
                      <button className="w-7 h-7 rounded-md flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-200/70 cursor-pointer" title="İndir"><i className="ri-download-line text-sm"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="md:hidden divide-y divide-background-200/70">
          {filtered.length === 0 ? (
            <div className="px-4 py-8 text-center text-foreground-400 text-sm">Fatura bulunamadı.</div>
          ) : (
            filtered.map((inv) => (
              <div key={inv.id} className="px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground-900">{inv.invoiceNumber}</p>
                    <p className="text-xs text-foreground-500 mt-0.5">{inv.customerName}</p>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-xs text-foreground-400">{new Date(inv.date).toLocaleDateString("tr-TR")}</span>
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${statusColors[inv.status] || "bg-background-100 text-foreground-600"}`}>{inv.status}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-sm font-bold text-foreground-950">${inv.amount.toLocaleString("en-US")}</p>
                    <div className="flex items-center gap-1 mt-1.5 justify-end">
                      <button className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-primary-600 hover:bg-primary-100 cursor-pointer"><i className="ri-eye-line"></i></button>
                      <button className="w-8 h-8 rounded-md flex items-center justify-center text-foreground-400 hover:text-foreground-600 hover:bg-background-200/70 cursor-pointer"><i className="ri-download-line"></i></button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}